import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Check, CaretDown } from "@phosphor-icons/react";
import { LANGUAGES } from "@/i18n/languages";
import { useLanguage } from "@/i18n/LanguageContext";

export default function LanguageSwitcher({ variant = "desktop" }) {
  const { lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  return (
    <div ref={ref} data-no-translate className={`relative ${variant === "mobile" ? "w-full" : ""}`}>
      <button
        data-testid="language-switcher-button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Change language"
        className={
          variant === "mobile"
            ? "flex items-center gap-2 text-emerald font-display text-2xl"
            : "flex items-center gap-1.5 text-sm tracking-wide text-emerald hover:text-sunset transition-colors duration-300"
        }
      >
        <Globe size={variant === "mobile" ? 24 : 18} weight="duotone" className="text-sunset" />
        <span>{current.native}</span>
        <CaretDown
          size={variant === "mobile" ? 16 : 12}
          weight="bold"
          className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.ul
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            data-testid="language-switcher-menu"
            className={`${
              variant === "mobile"
                ? "relative mt-3 w-full"
                : "absolute right-0 top-full mt-3 w-52 shadow-[0_20px_60px_-25px_rgba(15,26,21,0.45)]"
            } z-50 rounded-2xl bg-sand/98 backdrop-blur-xl border border-emerald/10 p-1.5 overflow-hidden`}
          >
            {LANGUAGES.map((l) => (
              <li key={l.code}>
                <button
                  data-testid={`language-option-${l.code}`}
                  onClick={() => {
                    setLang(l.code);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center justify-between gap-3 rounded-xl px-3.5 py-2.5 text-left transition-colors duration-200 ${
                    lang === l.code
                      ? "bg-sunset/12 text-sunset"
                      : "text-emerald hover:bg-emerald/5"
                  }`}
                >
                  <span className="flex flex-col">
                    <span className="text-sm font-medium leading-tight">{l.native}</span>
                    <span className="text-[11px] text-emerald-soft leading-tight">{l.label}</span>
                  </span>
                  {lang === l.code && <Check size={16} weight="bold" className="text-sunset shrink-0" />}
                </button>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}
